'use client'

import { SiTelegram } from '@icons-pack/react-simple-icons'
import { AlertTriangle, Loader2Icon } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useState } from 'react'

import { SettingsCard, SettingsCardHeader } from '@/components/dialog/settings/settings-card'
import { Button } from '@/components/ui/button'
import { useIsMobileDevice } from '@/hooks/use-screen'
import { toast } from '@/lib/toast'
import { useMintMessagingLinkNonce } from '@/service/session.hook'
import { SessionInfo } from '@/types/generated/common/auth'
import { MessagingChannel, MessagingSurface } from '@/types/generated/public_api'

// The backend has no separate "is this bot linked" query — the only signal is
// whether a Telegram session for that surface shows up in the existing
// session list, matched by its deviceLabel. SessionInfo (proto/common/auth.proto)
// carries no structured channel/surface field, so this string match is the
// only mechanism available today. MUST be confirmed against a real backend
// response before launch (exact em dash / spacing) — see plan doc.
const TELEGRAM_SURFACE_LABELS: Partial<Record<MessagingSurface, string>> = {
  [MessagingSurface.MESSAGING_SURFACE_RESEARCH]: 'Telegram — Research',
  [MessagingSurface.MESSAGING_SURFACE_NOTIFICATIONS]: 'Telegram — Notifications',
}

export function isSurfaceLinked(sessions: SessionInfo[], surface: MessagingSurface): boolean {
  const label = TELEGRAM_SURFACE_LABELS[surface]
  return !!label && sessions.some((session) => session.deviceLabel === label)
}

type ConnectOutcome = 'open-mobile' | 'open-desktop' | 'unavailable'

// Pure so it's unit-testable without touching window.open/location — the
// surrounding handler is the only place that actually performs navigation.
export function resolveConnectOutcome({
  isMobile,
  deepLink,
}: {
  isMobile: boolean
  deepLink?: string
}): ConnectOutcome {
  if (!deepLink) return 'unavailable'
  return isMobile ? 'open-mobile' : 'open-desktop'
}

const SURFACES = [
  MessagingSurface.MESSAGING_SURFACE_RESEARCH,
  MessagingSurface.MESSAGING_SURFACE_NOTIFICATIONS,
] as const

export function TelegramLinkingCard({
  sessions,
  isLoading,
  isError,
}: {
  sessions: SessionInfo[]
  isLoading: boolean
  isError: boolean
}) {
  const t = useTranslations('settings')

  return (
    <SettingsCard>
      <SettingsCardHeader
        icon={<SiTelegram size={16} className="text-foreground/80" />}
        title={t('notifications.telegram.title')}
        description={t('notifications.telegram.description')}
      />
      <div className="flex flex-col gap-3">
        {SURFACES.map((surface) => (
          <TelegramSurfaceRow
            key={surface}
            surface={surface}
            // Status is only trustworthy once the session list has actually
            // loaded — while loading or after a failed fetch, an empty
            // `sessions` array would otherwise look identical to "not linked".
            isLinked={!isLoading && !isError && isSurfaceLinked(sessions, surface)}
            statusUnknown={isError}
            disabled={isLoading}
          />
        ))}
      </div>
    </SettingsCard>
  )
}

type SurfaceState = 'idle' | 'pending' | 'unavailable'

function TelegramSurfaceRow({
  surface,
  isLinked,
  statusUnknown,
  disabled,
}: {
  surface: MessagingSurface
  isLinked: boolean
  statusUnknown: boolean
  disabled: boolean
}) {
  const t = useTranslations('settings')
  const isMobile = useIsMobileDevice()
  const [state, setState] = useState<SurfaceState>('idle')

  const { mutateAsync: mint, isPending } = useMintMessagingLinkNonce({
    onError: (error) => {
      toast.error(error.message || t('notifications.telegram.connectFailed'))
      setState('idle')
    },
  })

  const label =
    surface === MessagingSurface.MESSAGING_SURFACE_RESEARCH
      ? t('notifications.telegram.researchLabel')
      : t('notifications.telegram.notificationsLabel')

  const handleConnect = async () => {
    if (state !== 'idle') return

    // Desktop: open a blank popup synchronously, inside the click handler,
    // before the await below — this is the last moment the browser's user
    // activation from the click is guaranteed to still be alive. Opening
    // AFTER awaiting the mint response risks the popup blocker eating an
    // otherwise-successful response (see plan doc §4a — do NOT "fix" this by
    // copying hooks/use-venue-oauth-connect.ts's open-after-await order).
    // Mobile skips the popup entirely and navigates the current tab instead,
    // matching how a t.me applink hand-off is normally done there.
    const popup = isMobile ? null : window.open('', '_blank')
    if (!isMobile && !popup) {
      toast.error(t('notifications.telegram.popupBlocked'))
      return
    }

    setState('pending')
    try {
      const response = await mint({
        channel: MessagingChannel.MESSAGING_CHANNEL_TELEGRAM,
        surface,
      })
      const outcome = resolveConnectOutcome({ isMobile, deepLink: response.deepLink })
      if (outcome === 'unavailable') {
        popup?.close()
        setState('unavailable')
        return
      }
      if (outcome === 'open-mobile') {
        window.location.assign(response.deepLink!)
      } else {
        popup!.opener = null
        popup!.location.replace(response.deepLink!)
      }
      setState('idle')
    } catch {
      // onError above already toasted and reset state.
      popup?.close()
    }
  }

  if (state === 'unavailable') {
    return (
      <div className="flex items-center justify-between gap-4">
        <span className="text-foreground text-sm tracking-tight">{label}</span>
        <span className="text-foreground/40 text-xs">
          {t('notifications.telegram.unavailable')}
        </span>
      </div>
    )
  }

  return (
    <div className="flex items-center justify-between gap-4">
      <div className="flex items-center gap-1.5">
        <span className="text-foreground text-sm tracking-tight">{label}</span>
        {statusUnknown && (
          <AlertTriangle
            className="text-ox-gold size-3.5"
            aria-label={t('notifications.telegram.statusUnknown')}
          />
        )}
      </div>
      <Button
        variant="secondary"
        size="sm"
        disabled={disabled || isPending}
        onClick={handleConnect}
      >
        {isPending ? (
          <Loader2Icon className="size-3.5 animate-spin" />
        ) : isLinked ? (
          t('notifications.telegram.reconnect')
        ) : (
          t('notifications.telegram.connect')
        )}
      </Button>
    </div>
  )
}
